/* onezerofive — entrance choreography.

   The grid deals itself out. Each row starts as a stack at its own left edge,
   the tiles overlapped and slightly turned, and they slide right one after the
   other and unwind into their columns — fast out of the stack, slow into the
   slot. Rows only begin when they reach the viewport, so a long grid keeps
   dealing as you scroll instead of finishing unseen above the fold.

   Two supporting jobs: a photograph fades in when it has decoded, so the
   picture and the tile holding it arrive together rather than the frame
   animating and the image snapping in behind it; and when the browser has run
   a cross-document view transition it already owns the arrival, so the page's
   own fade stands down for that frame.

   Everything degrades: without JS nothing is hidden, and with reduced motion
   everything is shown at once. */
(function () {
  const root = document.documentElement;
  root.classList.add('js');   // normally already set inline in <head>, before first paint

  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Going back is not an arrival. On a back or forward navigation the browser
     cross-fades a snapshot of the page you just left into this one — and if the
     grid then deals itself out again, the snapshot's full grid fades away to
     nothing before the tiles come back. That vanish-and-replay is the glitch:
     forward feels like a glide because the destination is new, back does not
     because you have already seen it. A restored page is shown, not dealt. */
  const navType = ((performance.getEntriesByType('navigation') || [])[0] || {}).type;
  const restored = navType === 'back_forward';
  if (restored) root.classList.add('oz-restored');   // no transition either — a return is instant
  const CARDS = '.tile, .sys';
  const SHOTS = '.tile .pic img, .sys .pic img';

  /* How far back toward the stack a tile starts. Full overlap looks frantic
     across a 1240px row, so it is a fraction of the real distance, capped. */
  const narrow = () => window.innerWidth <= 720;
  const PULL = () => (narrow() ? 0.4 : 0.55);          // fraction of the real distance back
  const PULL_MAX = () => (narrow() ? 120 : 360);       // px — a phone is not a 1240px row
  const STEP = () => (narrow() ? 60 : 80);             // ms between one tile and the next
  const TURN = () => (narrow() ? 0.9 : 1.4);           // degrees of tilt per place back

  /* ---------- photographs ---------- */
  function watchImages() {
    document.querySelectorAll(SHOTS).forEach((img) => {
      if (img.dataset.ozSeen) return;
      img.dataset.ozSeen = '1';
      const settle = () => img.classList.add('in');
      if (restored || (img.complete && img.naturalWidth)) { settle(); return; }
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });
  }

  /* ---------- measure each row's stack ---------- */
  function cardsIn(grid) {
    return Array.prototype.filter.call(grid.children, (el) => el.matches(CARDS));
  }

  function measure() {
    document.querySelectorAll('.grid, .systems').forEach((grid) => {
      const cards = cardsIn(grid);
      if (!cards.length) return;
      const rows = new Map();
      cards.forEach((c) => {
        const top = c.offsetTop;
        if (!rows.has(top)) rows.set(top, []);
        rows.get(top).push(c);
      });
      rows.forEach((row) => {
        const stack = row[0].offsetLeft;
        row.forEach((c, i) => {
          if (c.classList.contains('in')) return;   // already landed, leave it
          const back = Math.min((c.offsetLeft - stack) * PULL(), PULL_MAX());
          c.style.setProperty('--dx', (-back).toFixed(1) + 'px');
          c.style.setProperty('--rot', (-(1 + i * TURN())).toFixed(2) + 'deg');
          c.dataset.ozPlace = i;                    // deal order, left to right
        });
      });
    });
  }

  /* ---------- deal ---------- */
  function land(el) {
    const place = Number(el.dataset.ozPlace || 0);
    void el.offsetWidth;      // commit the start state, or there is nothing to transition from
    const wait = place * STEP();
    el.style.setProperty('--d', wait + 'ms');
    el.classList.add('in');
    // clear the entrance values once it has landed, or the hover dim inherits them
    setTimeout(() => {
      el.style.removeProperty('--d');
      el.style.removeProperty('--dx');
      el.style.removeProperty('--rot');
    }, wait + 1200);
  }

  let held = false;

  function watchCards() {
    if (held) return;
    const cards = document.querySelectorAll(CARDS);
    if (!cards.length) return;
    if (still || restored || !('IntersectionObserver' in window)) {
      cards.forEach((c) => c.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        land(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
    cards.forEach((c) => io.observe(c));
  }

  /* Safety net. IntersectionObserver does not run while a tab is hidden, and a
     grid that never lands is far worse than one that never animates. Once the
     page is actually visible, anything still waiting inside the viewport is
     dealt regardless. Tiles below the fold are left for the scroll. */
  function net() {
    if (document.visibilityState !== 'visible') return;
    setTimeout(() => {
      document.querySelectorAll(CARDS).forEach((c) => {
        if (c.classList.contains('in')) return;
        if (c.getBoundingClientRect().top < window.innerHeight) land(c);
      });
    }, 2200);
  }

  function init() {
    watchImages();
    measure();
    /* pagereveal fires after DOMContentLoaded and before the first paint, so the
       observer waits a frame — otherwise it would start before we know whether a
       view transition is about to own the screen. */
    requestAnimationFrame(watchCards);
    net();
  }

  document.addEventListener('visibilitychange', net);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* a webfont swapping in reflows the grid, and rows are grouped by offsetTop —
     measure again once the faces have settled, for anything still waiting */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);

  /* the column count changes with the width, so anything still waiting needs
     its distance back to the stack measured again */
  let t;
  window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(measure, 150); });

  /* restored straight out of the back/forward cache: the scripts never re-run,
     but make sure nothing is left hidden if they did */
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll(CARDS).forEach((c) => c.classList.add('in'));
    document.querySelectorAll(SHOTS).forEach((i) => i.classList.add('in'));
  });

  /* ---------- one entrance, not two ---------- */
  window.addEventListener('pagereveal', (e) => {
    if (!e.viewTransition) return;
    /* Not removed when the transition finishes. The class cancels entrance
       animations rather than pausing them, so taking it off mid-page would
       start them from zero at the moment the flight lands — which is the
       glitch, not the cure. A view transition owned this arrival; that stays
       true for the rest of the page's life. */
    root.classList.add('vt');
  });

  /* If a transition is running after all — a product opening — hold the deal
     until it has finished, so the grid never animates underneath a snapshot. */
  window.addEventListener('pagereveal', (e) => {
    if (!e.viewTransition || restored) return;
    held = true;
    const release = () => { if (!held) return; held = false; watchCards(); };
    e.viewTransition.finished.finally(release);
    setTimeout(release, 1500);   // never let a promise that does not settle strand the grid
  });
})();

/* onezerofive — the pointer is the mark.

   The circumpunct from brand guide 01: two concentric rings and a centre dot.
   The pip tracks the pointer exactly; the rings trail a beat behind, which is
   what makes it read as an object rather than a graphic stuck to the mouse. The
   trail is capped — each frame the gap is measured against the inner ring's own
   radius and the rings are pulled along if the pip would otherwise escape it.

   Over anything live the outer ring opens, the inner draws in toward the pip,
   and a third ring leaves the centre and dies at the rim on a slow loop. On
   press the whole mark collapses inward.

   Fine pointers only. Touch and reduced-motion never see it, and the native
   cursor is only hidden once this has taken over. */
(function () {
  if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var cur = document.querySelector('.cur');
  if (!cur) return;
  var pip = cur.querySelector('.pip');
  var rings = cur.querySelector('.rings');
  var outer = cur.querySelector('.o');
  var inner = cur.querySelector('.i');
  if (!pip || !rings || !outer || !inner) return;

  /* the mark is drawn in a 100-unit box rendered at 36px: outer r46, inner r30,
     stroke 5.6 — so the inner ring sits 10.8px out and its stroke is 2px wide */
  var INNER = 30 * 0.36;
  var EDGE = (5.6 * 0.36) / 2 + 2;   // half the stroke, plus the pip's radius

  var x = innerWidth / 2, y = innerHeight / 2, rx = x, ry = y, on = false;
  var so = 1, tso = 1;   // outer scale
  var si = 1, tsi = 1;   // inner scale

  document.documentElement.classList.add('cursoring');

  /* Shopify's cookie-consent banner and the theme's own dark buttons (ADD,
     a selected variant, the hero) can put the grey mark on a grey-on-grey
     background where it disappears. Walk up from whatever is under the
     pointer for the first opaque background and read it as dark or light,
     rather than hard-coding a selector list that would need updating every
     time a new dark surface is added. */
  function overDark(el) {
    var node = el, hops = 0;
    while (node && node !== document.documentElement && hops++ < 8) {
      var bg = getComputedStyle(node).backgroundColor;
      var m = bg && bg.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
      if (m) {
        var a = m[4] === undefined ? 1 : parseFloat(m[4]);
        if (a > 0.4) {
          var r = +m[1], g = +m[2], b = +m[3];
          return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
        }
      }
      node = node.parentElement;
    }
    return false;
  }

  addEventListener('pointermove', function (e) {
    x = e.clientX; y = e.clientY;
    if (!on) { on = true; rx = x; ry = y; cur.classList.add('live'); }
    var t = e.target.closest && e.target.closest('a,button,input,select,textarea,label,summary,[data-dot],[role=button]');
    cur.classList.toggle('near', !!t);
    cur.classList.toggle('on-dark', overDark(e.target));
  }, { passive: true });

  addEventListener('pointerdown', function () { cur.classList.add('press'); }, { passive: true });
  addEventListener('pointerup', function () { cur.classList.remove('press'); }, { passive: true });
  addEventListener('pointerleave', function () { cur.classList.remove('live'); on = false; });
  addEventListener('blur', function () { cur.classList.remove('live'); on = false; });

  (function frame() {
    var near = cur.classList.contains('near');
    var press = cur.classList.contains('press');
    tso = press ? 0.5 : (near ? 1.3 : 1);
    tsi = press ? 0.38 : (near ? 0.86 : 1);
    so += (tso - so) * 0.18;
    si += (tsi - si) * 0.18;

    rx += (x - rx) * 0.18;
    ry += (y - ry) * 0.18;

    var dx = rx - x, dy = ry - y;
    var d = Math.sqrt(dx * dx + dy * dy);
    var max = Math.max(0, INNER * si - EDGE);
    if (d > max) { var k = max / d; rx = x + dx * k; ry = y + dy * k; }

    pip.style.transform = 'translate(' + x + 'px,' + y + 'px)';
    rings.style.transform = 'translate(' + rx + 'px,' + ry + 'px)';
    outer.style.transform = 'scale(' + so.toFixed(3) + ')';
    inner.style.transform = 'scale(' + si.toFixed(3) + ')';
    requestAnimationFrame(frame);
  })();
})();
